import { useState, useCallback } from 'react';
import { message } from 'antd';
import { l316Api, ResultRow, FilterParams } from '../../../services/l316Api';

export interface L316FilterState {
  startNo: string;
  lastNo: string;
}

export const emptyFilter = (): L316FilterState => ({
  startNo: '',
  lastNo: 'zzzzzzz',
});

/**
 * Auto-fill lastNo per BR-C01:
 * SUBSTR(ALLTRIM(startNo) + "zzzzzzz", 1, 7)
 */
export const autoFillLastNo = (startNo: string): string => {
  const trimmed = startNo.trim();
  if (!trimmed) return 'zzzzzzz';
  const padded = trimmed + 'zzzzzzz';
  return padded.substring(0, 7);
};

export const useL316Query = () => {
  const [filter, setFilter] = useState<L316FilterState>(emptyFilter());
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  const setField = useCallback(
    <K extends keyof L316FilterState>(field: K, value: L316FilterState[K]) => {
      setFilter((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const onStartNoBlur = useCallback(() => {
    setFilter((prev) => ({
      ...prev,
      lastNo: autoFillLastNo(prev.startNo),
    }));
  }, []);

  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const params: FilterParams = {};
      if (filter.startNo.trim()) params.startNo = filter.startNo.trim();
      if (filter.lastNo.trim()) params.lastNo = filter.lastNo.trim();

      const res = await l316Api.getReport(params);
      setRows(res.items || []);
      setQueried(true);
      if (!res.items || res.items.length === 0) {
        message.info('查無資料!');
      } else {
        message.success(`查詢到 ${res.total} 筆記錄`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const onClear = useCallback(() => {
    setFilter(emptyFilter());
    setRows([]);
    setQueried(false);
  }, []);

  return {
    filter,
    setField,
    onStartNoBlur,
    rows,
    loading,
    queried,
    onQuery,
    onClear,
  };
};
